/**
 * Custom Next.js image loader — routes optimization through Supabase Imgproxy
 * instead of Next.js built-in Sharp-based optimizer.
 *
 * How it works:
 * - Next.js <Image> calls this loader for each width in the srcSet
 * - We return a URL pointing to Supabase's /storage/v1/render/image/public/ endpoint
 * - Supabase Imgproxy resizes, converts to WebP/AVIF, and caches the result
 * - In dev: Next.js rewrites /storage/* → Supabase (localhost:54321)
 * - In prod: Caddy routes /storage/v1/render/* → Supabase (localhost:8000)
 *
 * Supported src formats:
 * - /api/storage/bucket/path  (our storage proxy URLs)
 * - Full Supabase URLs (https://...supabase.co/storage/v1/object/public/...)
 * - External URLs (passed through unchanged)
 */

interface ImageLoaderParams {
  src: string;
  width: number;
  quality?: number;
}

/**
 * Build the render query, carrying over any params the source URL already had.
 *
 * A source URL can arrive with a query string: an object replaced in place by
 * an operator gets a `?v=`/`?c=` suffix so Cloudflare sees a fresh cache key
 * (see the storage route). Appending `?width=…` to that produced
 * `...png?v=2?width=384&quality=80`, which the render endpoint reads as a
 * single `v=2?width=384` param — `width` never lands, and the full-size
 * original is served for every srcSet candidate.
 *
 * So split the query off first and re-attach it after `width`/`quality`. The
 * suffix is inert at the render endpoint but still varies the CDN cache key,
 * which is the whole point of it being there — dropping it would let a stale
 * pre-replacement entry answer again.
 */
function buildRenderQuery(search: string, width: number, quality: number): string {
  const base = `width=${width}&quality=${quality}`;
  if (!search) return base;

  // Drop any width/quality the caller already had — ours win.
  const carried = search
    .split('&')
    .filter((p) => p && !/^(width|quality)=/.test(p))
    .join('&');

  return carried ? `${base}&${carried}` : base;
}

/** Split a URL into its path and its query string (without the `?`). */
function splitQuery(src: string): [string, string] {
  const i = src.indexOf('?');
  return i === -1 ? [src, ''] : [src.slice(0, i), src.slice(i + 1)];
}

export default function imgproxyLoader({ src, width, quality }: ImageLoaderParams): string {
  const q = quality || 75;

  // Our storage proxy URLs: /api/storage/bucket/path → /storage/v1/render/image/public/bucket/path
  if (src.startsWith('/api/storage/')) {
    const [path, search] = splitQuery(src.slice('/api/storage/'.length));
    return `/storage/v1/render/image/public/${path}?${buildRenderQuery(search, width, q)}`;
  }

  // Full Supabase storage URLs
  const objectMarker = '/storage/v1/object/public/';
  const objectIdx = src.indexOf(objectMarker);
  if (objectIdx !== -1) {
    const [path, search] = splitQuery(src.slice(objectIdx + objectMarker.length));
    return `/storage/v1/render/image/public/${path}?${buildRenderQuery(search, width, q)}`;
  }

  // Already an Imgproxy render URL — update params
  if (src.includes('/storage/v1/render/')) {
    const [base, search] = splitQuery(src);
    return `${base}?${buildRenderQuery(search, width, q)}`;
  }

  // External/data URLs — pass through unchanged
  return src;
}
